import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { User, Mail, Phone, Car, Calendar, DollarSign, MessageCircle, ChevronDown, Search, Filter, RefreshCw, Trash2, X, AlertTriangle, CheckCircle, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserRequests, STATUS_LABELS, RequestStatus, CarRequest } from '../hooks/useUserRequests';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
interface AdminPageProps {
  onNavigate: (page: string) => void;
}
const EMAILJS_SERVICE_ID = 'service_m7m0m09';
const EMAILJS_TEMPLATE_ID = 'template_hyqrklh';
const EMAILJS_CLIENT_TEMPLATE_ID = 'template_qcpayxn';
const EMAILJS_PUBLIC_KEY = 'w8t9KyY5LzV6mitFs';
const statusColors: Record<RequestStatus, string> = {
  received: 'bg-gray-100 text-gray-800',
  searching: 'bg-blue-100 text-blue-800',
  auction: 'bg-indigo-100 text-indigo-800',
  purchased: 'bg-green-100 text-green-800',
  shipping: 'bg-yellow-100 text-yellow-800',
  customs: 'bg-orange-100 text-orange-800',
  delivered: 'bg-teal-100 text-teal-800'
};
const DENIAL_REASONS = ['Vehículo no disponible en el mercado actual', 'Presupuesto insuficiente para el modelo solicitado', 'Modelo no cumple requisitos de importación de El Salvador', 'Información de contacto incorrecta o incompleta', 'Solicitud duplicada', 'Otro (especificar)'];
export function AdminPage({
  onNavigate
}: AdminPageProps) {
  const {
    requests,
    loading,
    updateStatus,
    deleteRequest,
    refreshRequests
  } = useUserRequests();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  // Deny modal state
  const [denyTarget, setDenyTarget] = useState<CarRequest | null>(null);
  const [denyReason, setDenyReason] = useState(DENIAL_REASONS[0]);
  const [customReason, setCustomReason] = useState('');
  const [isDenying, setIsDenying] = useState(false);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 4000);
  };
  const filteredRequests = requests.filter(req => {
    const matchesSearch = (req.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (req.email || '').toLowerCase().includes(searchTerm.toLowerCase()) || req.car.make.toLowerCase().includes(searchTerm.toLowerCase()) || req.car.model.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Send status update email to client
  const sendStatusEmail = async (req: CarRequest, newStatus: RequestStatus) => {
    const carName = `${req.car.year} ${req.car.make} ${req.car.model}`;
    const newLabel = STATUS_LABELS[newStatus];
    const oldLabel = STATUS_LABELS[req.status];
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_CLIENT_TEMPLATE_ID, {
        to_email: req.email,
        subject: `Actualización de tu solicitud: ${carName} → ${newLabel}`,
        message: `Hola ${req.name},

Tu solicitud para el ${carName} (${req.car.trim}) ha sido actualizada.

Estado anterior: ${oldLabel}
Nuevo estado: ${newLabel}

Si tienes preguntas, contáctanos por WhatsApp:
https://wa.me/50379473074

— EARIA Import Auto`
      }, EMAILJS_PUBLIC_KEY);
      return true;
    } catch (err) {
      console.error('Error sending status email:', err);
      return false;
    }
  };
  // Send denial email to client
  const sendDenialEmail = async (req: CarRequest, reason: string) => {
    const carName = `${req.car.year} ${req.car.make} ${req.car.model}`;
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_CLIENT_TEMPLATE_ID, {
        to_email: req.email,
        subject: `Actualización sobre tu solicitud: ${carName}`,
        message: `Hola ${req.name},

Lamentamos informarte que tu solicitud para el ${carName} (${req.car.trim}) no puede ser procesada en este momento.

Motivo: ${reason}

Si deseas explorar otras opciones o tienes preguntas, contáctanos:
https://wa.me/50379473074

Estamos aquí para ayudarte a encontrar el vehículo ideal.

— EARIA Import Auto`
      }, EMAILJS_PUBLIC_KEY);
      return true;
    } catch (err) {
      console.error('Error sending denial email:', err);
      return false;
    }
  };
  const handleStatusChange = async (id: string, newStatus: string) => {
    const req = requests.find(r => r.id === id);
    if (!req || req.status === newStatus) return;
    setUpdatingId(id);
    const success = await updateStatus(id, newStatus as RequestStatus);
    if (success) {
      // Send email notification
      const emailSent = await sendStatusEmail(req, newStatus as RequestStatus);
      showToast(emailSent ? `Estado actualizado y correo enviado a ${req.name}` : `Estado actualizado (error al enviar correo)`, emailSent ? 'success' : 'error');
    } else {
      showToast('Error al actualizar el estado', 'error');
    }
    setUpdatingId(null);
  };
  const handleDeny = async () => {
    if (!denyTarget) return;
    setIsDenying(true);
    const finalReason = denyReason === 'Otro (especificar)' ? customReason : denyReason;
    if (!finalReason.trim()) {
      showToast('Por favor ingresa un motivo', 'error');
      setIsDenying(false);
      return;
    }
    // Send denial email first
    const emailSent = await sendDenialEmail(denyTarget, finalReason);
    // Then delete from database
    const deleted = await deleteRequest(denyTarget.id);
    if (deleted) {
      showToast(emailSent ? `Solicitud denegada y correo enviado a ${denyTarget.name}` : `Solicitud eliminada (error al enviar correo)`, emailSent ? 'success' : 'error');
    } else {
      showToast('Error al eliminar la solicitud', 'error');
    }
    setDenyTarget(null);
    setDenyReason(DENIAL_REASONS[0]);
    setCustomReason('');
    setIsDenying(false);
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-429">
      <Navbar onNavigate={onNavigate} data-id="element-430" />

      {/* Toast notification */}
      <AnimatePresence data-id="element-431">
        {toast && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-20 right-4 z-50" data-id="element-432">
            <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-id="element-433">
              {toast.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-600" data-id="element-434" /> : <AlertTriangle className="w-5 h-5 text-red-500" data-id="element-435" />}
              <span className="text-sm font-medium" data-id="element-436">{toast.message}</span>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Deny Modal */}
      <AnimatePresence data-id="element-437">
        {denyTarget && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={() => !isDenying && setDenyTarget(null)} data-id="element-438">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-439">
              {/* Modal Header */}
              <div className="bg-red-50 px-6 py-4 border-b border-red-100 flex items-center justify-between" data-id="element-440">
                <div className="flex items-center gap-3" data-id="element-441">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-442">
                    <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-443" />
                  </div>
                  <div data-id="element-444">
                    <h3 className="font-bold text-gray-900" data-id="element-445">
                      Denegar Solicitud
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-446">
                      Se enviará un correo al cliente
                    </p>
                  </div>
                </div>
                <button onClick={() => !isDenying && setDenyTarget(null)} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-447">
                  <X className="w-5 h-5" data-id="element-448" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-5 space-y-5" data-id="element-449">
                {/* Request summary */}
                <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4" data-id="element-450">
                  <div className="w-16 h-12 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0" data-id="element-451">
                    {denyTarget.car.image ? <img src={denyTarget.car.image} alt="" className="w-full h-full object-cover" data-id="element-452" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-453">
                        <Car className="w-6 h-6 text-gray-400" data-id="element-454" />
                      </div>}
                  </div>
                  <div data-id="element-455">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-456">
                      {denyTarget.car.year} {denyTarget.car.make}{' '}
                      {denyTarget.car.model}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-457">
                      Cliente: {denyTarget.name} · {denyTarget.email}
                    </p>
                  </div>
                </div>

                {/* Reason selector */}
                <div data-id="element-458">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-459">
                    Motivo de la denegación
                  </label>
                  <div className="space-y-2" data-id="element-460">
                    {DENIAL_REASONS.map(reason => <label key={reason} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${denyReason === reason ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-461">
                        <input type="radio" name="denyReason" value={reason} checked={denyReason === reason} onChange={() => setDenyReason(reason)} className="text-red-600 focus:ring-red-500" data-id="element-462" />
                        <span className="text-sm text-gray-700" data-id="element-463">{reason}</span>
                      </label>)}
                  </div>
                </div>

                {/* Custom reason input */}
                <AnimatePresence data-id="element-464">
                  {denyReason === 'Otro (especificar)' && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-465">
                      <textarea value={customReason} onChange={e => setCustomReason(e.target.value)} placeholder="Escribe el motivo de la denegación..." rows={3} className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500" data-id="element-466" />
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3" data-id="element-467">
                <button onClick={() => setDenyTarget(null)} disabled={isDenying} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50" data-id="element-468">
                  Cancelar
                </button>
                <button onClick={handleDeny} disabled={isDenying} className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 shadow-sm" data-id="element-469">
                  {isDenying ? <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" data-id="element-470"></div>
                      Procesando...
                    </> : <>
                      <Send className="w-4 h-4" data-id="element-471" />
                      Denegar y Notificar
                    </>}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="flex-1 pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-id="element-472">
        <div className="max-w-7xl mx-auto" data-id="element-473">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-474">
            <div data-id="element-475">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-476">
                Panel de Administración
              </h1>
              <p className="text-gray-500 mt-1" data-id="element-477">
                Gestiona las solicitudes de importación (
                {filteredRequests.length} solicitudes)
              </p>
            </div>
            <button onClick={() => refreshRequests()} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-478">
              <RefreshCw className="w-4 h-4" data-id="element-479" />
              Actualizar
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6 flex flex-col md:flex-row gap-4" data-id="element-480">
            <div className="flex-1 relative" data-id="element-481">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-482" />
              <input type="text" placeholder="Buscar por nombre, correo o vehículo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-483" />
            </div>
            <div className="w-full md:w-64 relative" data-id="element-484">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-485" />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white" data-id="element-486">
                <option value="all" data-id="element-487">Todos los estados</option>
                {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key} data-id="element-488">
                    {label}
                  </option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-489" />
            </div>
          </div>

          {/* Requests List */}
          {loading ? <div className="text-center py-12" data-id="element-490">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" data-id="element-491"></div>
              <p className="text-gray-500" data-id="element-492">Cargando solicitudes...</p>
            </div> : filteredRequests.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200 border-dashed" data-id="element-493">
              <p className="text-gray-500" data-id="element-494">No se encontraron solicitudes.</p>
            </div> : <div className="space-y-4" data-id="element-495">
              {filteredRequests.map(req => <div key={req.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-id="element-496">
                  <div className="p-6 flex flex-col lg:flex-row gap-6" data-id="element-497">
                    {/* Car Image */}
                    <div className="w-full lg:w-48 h-32 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-498">
                      {req.car.image ? <img src={req.car.image} alt={`${req.car.make} ${req.car.model}`} className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).style.display = 'none';
                }} data-id="element-499" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-500">
                          <Car className="w-12 h-12 text-gray-300" data-id="element-501" />
                        </div>}
                    </div>

                    {/* Details */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-502">
                      <div data-id="element-503">
                        <div className="flex items-center gap-2 mb-2" data-id="element-504">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-505">
                            {req.car.year} {req.car.make} {req.car.model}
                          </h3>
                          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-id="element-506">
                            {req.car.trim}
                          </span>
                        </div>

                        <div className="space-y-2 text-sm text-gray-600" data-id="element-507">
                          <div className="flex items-center gap-2" data-id="element-508">
                            <User className="w-4 h-4 text-gray-400" data-id="element-509" />
                            <span className="font-medium text-gray-900" data-id="element-510">
                              {req.name || 'Sin nombre'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-511">
                            <Mail className="w-4 h-4 text-gray-400" data-id="element-512" />
                            <a href={`mailto:${req.email}`} className="hover:text-blue-600" data-id="element-513">
                              {req.email || 'Sin correo'}
                            </a>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-514">
                            <Phone className="w-4 h-4 text-gray-400" data-id="element-515" />
                            <a href={`tel:${req.phone}`} className="hover:text-blue-600" data-id="element-516">
                              {req.phone || 'Sin teléfono'}
                            </a>
                            {req.phone && <a href={`https://wa.me/${req.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 bg-green-50 p-1 rounded-full" title="Chat en WhatsApp" data-id="element-517">
                                <MessageCircle className="w-4 h-4" data-id="element-518" />
                              </a>}
                          </div>
                        </div>
                      </div>

                      <div data-id="element-519">
                        <div className="space-y-2 text-sm text-gray-600 mb-4" data-id="element-520">
                          <div className="flex items-center gap-2" data-id="element-521">
                            <DollarSign className="w-4 h-4 text-gray-400" data-id="element-522" />
                            <span className="font-medium text-green-600" data-id="element-523">
                              ${req.priceRange.min.toLocaleString()} - $
                              {req.priceRange.max.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-524">
                            <Calendar className="w-4 h-4 text-gray-400" data-id="element-525" />
                            <span data-id="element-526">
                              {new Date(req.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                            </span>
                          </div>
                        </div>

                        {/* Status Control */}
                        <div data-id="element-527">
                          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider" data-id="element-528">
                            Estado del proceso
                          </label>
                          <div className="relative" data-id="element-529">
                            <select value={req.status} onChange={e => handleStatusChange(req.id, e.target.value)} disabled={updatingId === req.id} className={`w-full appearance-none pl-3 pr-10 py-2 rounded-lg text-sm font-medium border border-transparent transition-colors cursor-pointer ${statusColors[req.status]} hover:opacity-90 focus:ring-2 focus:ring-offset-1 focus:ring-blue-500`} data-id="element-530">
                              {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key} data-id="element-531">
                                    {label}
                                  </option>)}
                            </select>
                            {updatingId === req.id ? <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-532">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" data-id="element-533"></div>
                              </div> : <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50 pointer-events-none" data-id="element-534" />}
                          </div>
                        </div>

                        {/* Deny button */}
                        <button onClick={() => setDenyTarget(req)} className="mt-3 flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors" data-id="element-535">
                          <Trash2 className="w-3.5 h-3.5" data-id="element-536" />
                          Denegar solicitud
                        </button>
                      </div>
                    </div>
                  </div>

                  {req.message && <div className="bg-gray-50 px-6 py-3 border-t border-gray-100" data-id="element-537">
                      <p className="text-sm text-gray-600 italic" data-id="element-538">
                        "{req.message}"
                      </p>
                    </div>}
                </div>)}
            </div>}
        </div>
      </div>
      <Footer data-id="element-539" />
    </div>;
}